/**
 * ArtifactStudioPage — the operational room where a host composes
 * a print-safe memory object from their event.
 *
 * Route: /event/:slug/artifacts/:family
 *   currently supports family = 'polaroid-strip'
 *
 * Mode mix (per Craig's lock 2026-05-14):
 *   • the page chrome (header, format selector, export controls)
 *     stays in Mode 2 graphite — operational stewardship
 *   • the artifact itself, rendered inside <PrintCanvas>, is pure
 *     Mode 1 atmospheric — it must read as a memory object, not
 *     as a UI surface
 *
 * Data:
 *   • event payload via GET /api/events/{slug}
 *   • the host's first 3 photos via GET /api/events/{event_id}/photos
 *   • photo URLs resolved via getMediaUrl() so R2/Emergent storage
 *     paths work transparently in the SVG preview
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MODE_TWO,
  PageShell, PageGutter, Eyebrow, EditorialHeading,
  QuietPress,
} from '../components/ModeTwoPrimitives';
import PrintCanvas from '../components/print/PrintCanvas';
import PolaroidStripArtifact from '../components/print/PolaroidStripArtifact';
import ArtifactPreviewFrame from '../components/print/ArtifactPreviewFrame';
import { DEFAULT_FORMAT_BY_FAMILY, getFormat } from '../lib/print/printFormats';
import { getMediaUrl } from '../lib/media';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Family → frontend metadata used by the studio. Backend endpoints
// are kept symmetrical (artifacts/polaroid-strip etc.).
const FAMILY_META = {
  'polaroid-strip': {
    family: 'polaroid_strip',
    label: 'Polaroid strip',
    exportEndpoint: (eventId) => `/events/${eventId}/artifacts/polaroid-strip`,
    requiredPhotos: 3,
  },
};

const ArtifactStudioPage = () => {
  const { slug, family } = useParams();
  const navigate = useNavigate();

  const meta = FAMILY_META[family];
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formatId, setFormatId] = useState(meta ? DEFAULT_FORMAT_BY_FAMILY[meta.family] : null);
  const [showMarks, setShowMarks] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const run = async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        const evt = r.data?.event || r.data;
        setEvent(evt);
        if (evt?.event_id) {
          try {
            const pr = await axios.get(`${API}/events/${evt.event_id}/photos`);
            if (!cancelled) {
              const list = Array.isArray(pr.data) ? pr.data : (pr.data?.photos || []);
              setPhotos(list);
            }
          } catch (_) { /* no photos yet is fine — preview shows ✦ slots */ }
        }
      } catch (_) {
        /* event not found / not authorised — render an empty studio */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [slug]);

  const fmt = useMemo(() => getFormat(formatId), [formatId]);

  // The artifact takes up to 3 photo URLs, already absolute, in
  // upload order. Future versions will let the host curate which
  // three; the foundation lays the channel.
  const photoUrls = useMemo(() => {
    return photos.slice(0, meta?.requiredPhotos || 3)
      .map((p) => getMediaUrl(p.storage_path || p.url || p));
  }, [photos, meta]);

  if (!meta) {
    return (
      <PageShell testId="artifact-studio-unknown">
        <PageGutter>
          <Eyebrow>✦ STUDIO</Eyebrow>
          <EditorialHeading size="h1">That artifact isn’t here yet.</EditorialHeading>
          <div style={{
            marginTop: 14, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 14, color: MODE_TWO.inkMuted,
          }}>
            The polaroid strip is the first artifact in the studio.
            Poster and guestbook are on their way.
          </div>
          <div style={{ marginTop: 22 }}>
            <QuietPress onClick={() => navigate(`/event/${slug}`)} testId="artifact-back">
              Back to the room ✦
            </QuietPress>
          </div>
        </PageGutter>
      </PageShell>
    );
  }

  return (
    <PageShell testId="artifact-studio">
      {/* Load the canonical EventDrop artifact serif (EB Garamond)
          for the SVG preview so the screen reads with the same
          warmth as the print export. The backend bundles the same
          family at 300 DPI; preview & press now share one voice. */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;1,400;1,500&display=swap"
        rel="stylesheet"
      />
      <PageGutter>
        <div style={{ marginBottom: 20 }}>
          <QuietPress
            variant="quiet"
            onClick={() => navigate(`/event/${slug}`)}
            testId="artifact-back"
          >← Back to the room ✦</QuietPress>
        </div>

        <Eyebrow>✦ THE STUDIO</Eyebrow>
        <EditorialHeading size="h1">
          A printed object, of this night.
        </EditorialHeading>
        <div style={{
          marginTop: 12, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 14, color: MODE_TWO.inkMuted, lineHeight: 1.55,
          maxWidth: 620, marginBottom: 28,
        }}>
          Compose a strip from {event?.event_name || 'tonight'}. What
          you see is what we’ll send to press — at scale, with bleed.
          No ordering yet; this is the archive’s draft.
        </div>

        <ArtifactPreviewFrame
          event={event}
          family={meta.family}
          artifactLabel={meta.label}
          exportEndpoint={meta.exportEndpoint(event?.event_id || '')}
          exportQuery={{}}
          formatId={formatId}
          onFormatChange={setFormatId}
          showMarks={showMarks}
          onShowMarksChange={setShowMarks}
          testId="artifact-preview-frame"
        >
          {fmt && (
            <PrintCanvas format={fmt} maxWidthPx={360} showMarks={showMarks}>
              {(geo) => (
                <PolaroidStripArtifact
                  geometry={geo}
                  event={event || {}}
                  photos={photoUrls}
                />
              )}
            </PrintCanvas>
          )}
        </ArtifactPreviewFrame>

        {!loading && photoUrls.length < (meta.requiredPhotos || 3) && (
          <div data-testid="artifact-photo-hint" style={{
            marginTop: 10, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 13, color: MODE_TWO.inkFaint, lineHeight: 1.55, maxWidth: 520,
          }}>
            ✦ Empty slots will fill themselves as the night gathers more memories.
            The strip composes around whatever has landed so far.
          </div>
        )}
      </PageGutter>
    </PageShell>
  );
};

export default ArtifactStudioPage;
